import { ArrowBack, MoreVert } from '@mui/icons-material'
import { Box, IconButton, Skeleton, Stack, SvgIcon, Typography } from '@mui/material'
import Image from 'next/image'
import { FC, Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { RecordConfidentialChip } from '@omnea-app/fe-authz/ConfidentialChip'
import { RENAME_REQUEST } from '@omnea-app/fe-authz/recordRules'
import { useIsPermitted } from '@omnea-app/fe-authz/useIsPermitted'
import { usePriceFormatter } from '@omnea-app/fe-i18n/usePriceFormatter'
import { CopyConfigButton } from '@omnea-app/fe-legacy/components/shared/ui/CopyConfigButton/CopyConfigButton'
import { CopyIdButton } from '@omnea-app/fe-legacy/components/shared/ui/CopyIdButton/CopyIdButton'
import { useFeatureFlags } from '@omnea-app/fe-org-settings/FeatureFlagsProvider'
import { InternalLinkIconButton } from '@omnea-app/fe-routing/InternalLinkIconButton'
import ProductPlaceholderIcon from '@omnea-app/icons/common/product-placeholder.svg'
import RenewalPlaceholderIcon from '@omnea-app/icons/common/renewal-placeholder.svg'
import { RequestTypeEnum } from '@omnea-app/packages/universal/hasura/graphql'
import { Price } from '@omnea-app/packages/universal/price/price'
import { RequestDetail, RequestDetailResponse } from '@omnea-app/requests/requestsFrontendClient'
import { useRequestLdFlags } from '../../../launch-darkly'
import { OperationalStatusChip, WithOverdueDays } from '../../../operational-status/OperationalStatusChip'
import { useShowOperationalStatus } from '../../../operational-status/useShowOperationalStatus'
import { generateRequestsListRoute } from '../../../requestRoutes'
import { EstimatedCompletionBanner } from '../estimated-completion-banner'
import { ManageMenu, ManageMenuAnchor } from '../manage-menu'
import { EditableRequestTitle } from './EditableRequestTitle'

export type RequestHeaderData = Pick<RequestDetailResponse, 'value' | 'participants' | 'inheritedFrom'> & {
  derivedFields: Pick<
    RequestDetail['derivedFields'],
    'product' | 'supplier' | 'model' | 'ownerId' | 'currentUserAssignees' | 'currentGroupAssignees' | 'operationalStatus'
  >
  rawData: Pick<
    RequestDetail['rawData'],
    'publicId' | 'name' | 'completedAt' | 'state' | 'type' | 'requestorUserId' | 'isConfidential'
  >
}

type RequestHeaderProps = {
  request?: RequestHeaderData
  isLoading: boolean
  requestTitle: string
  isRequestRejected: boolean
  isRequestCompleted: boolean
  requestId: string
  onRejectRequest: (reason: string) => void
  onUnrejectRequest: (reason: string, productId?: string) => void
  onDeleteRequest: () => void
}

const ManageMenuButton: ManageMenuAnchor = ({ buttonRef, openMenu }) => (
  <IconButton sx={{ p: 0 }} ref={buttonRef} onClick={openMenu}>
    <MoreVert />
  </IconButton>
)

export function useFormatRequestValue(value: Price | null | undefined) {
  const { formatPrice } = usePriceFormatter()
  if (!value) return null
  return formatPrice(value).formattedPrice
}

const ProductLogo = ({
  product,
  supplier,
  requestTitle,
  isRenewal,
}: {
  product: { logoUrl?: string | null; name?: string } | null | undefined
  supplier: { logoUrl?: string | null; name?: string } | null | undefined
  requestTitle: string
  isRenewal: boolean
}) => {
  const alt = product?.name || requestTitle

  const logoUrl = product?.logoUrl || supplier?.logoUrl

  if (!logoUrl) {
    return (
      <Stack
        data-testid="placeholder"
        sx={{
          borderRadius: 3,
          backgroundColor: 'bg.neutral.mutedHover',
          color: 'text.inverted',
          padding: 4,
          alignSelf: 'center',
        }}
      >
        <SvgIcon
          component={isRenewal ? RenewalPlaceholderIcon : ProductPlaceholderIcon}
          inheritViewBox
          aria-hidden="true"
          sx={{ fontSize: 24 }}
        />
      </Stack>
    )
  }
  return (
    <Box
      sx={{
        borderRadius: '6px',
        border: 'stroke.default',
        borderColor: 'border.subtle',
        overflow: 'hidden',
        backgroundColor: 'bg.surface.default',
        alignSelf: 'center',
      }}
    >
      <Image alt={alt} src={logoUrl} style={{ objectFit: 'contain' }} width={42} height={42} />
    </Box>
  )
}

export const RequestHeader: FC<RequestHeaderProps> = ({
  request,
  isLoading,
  requestTitle,
  isRequestRejected,
  isRequestCompleted,
  requestId,
  onDeleteRequest,
  onRejectRequest,
  onUnrejectRequest,
}) => {
  const product = request?.derivedFields?.product
  const supplier = request?.derivedFields?.supplier

  const subtitleDetails = [
    { text: supplier?.name, testId: 'request-supplier' },
    { text: useFormatRequestValue(request?.value), testId: 'request-value' },
    { text: request?.rawData.publicId, testId: 'request-public-id' },
  ].filter((detail) => !!detail.text)

  const { user } = useAuthenticatedUser()
  const canRename = useIsPermitted(RENAME_REQUEST, requestId)
  const { HIDE_REQUEST_ESTIMATED_SLA_DATE } = useFeatureFlags()
  const showOperationalStatus = useShowOperationalStatus()
  const { RELEASE_SIMPLIFIED_REQUEST_SUMMARIES } = useRequestLdFlags()
  // The title pill ships with the simplified request summaries.
  const operationalStatus =
    showOperationalStatus && RELEASE_SIMPLIFIED_REQUEST_SUMMARIES ? request?.derivedFields.operationalStatus : null
  const { t } = useTranslation('requests')

  return (
    <Stack
      gap={{ xs: 18, md: 4 }}
      direction={{ xs: 'column-reverse', md: 'row' }}
      alignItems={{ xs: 'stretch', md: 'center' }}
      sx={{ justifyContent: 'space-between' }}
      pt={{ md: 3 }}
      pb={{ xs: 18, md: 12 }}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={5} sx={{ flex: 1, minWidth: 0 }}>
        {isLoading ? (
          <Skeleton variant="rectangular" sx={{ borderRadius: '8px' }} width={350} height={40} />
        ) : (
          <>
            <ProductLogo
              product={product}
              supplier={supplier}
              requestTitle={requestTitle}
              isRenewal={request?.rawData.type === RequestTypeEnum.RENEWAL}
            />
            <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
              {/* Column on xs so the title keeps its full-width centred layout; the chip drops beneath it. */}
              <Stack direction={{ xs: 'column', md: 'row' }} alignItems="center" gap={3} sx={{ minWidth: 0 }}>
                <EditableRequestTitle requestTitle={requestTitle} requestId={requestId} disabled={!canRename} />
                {request != null && (
                  <RecordConfidentialChip
                    confidentiality={{ isConfidential: request.rawData.isConfidential, inheritedFrom: request.inheritedFrom }}
                    ownMark={{ label: t('confidentiality.chip'), tooltip: t('confidentiality.chip-tooltip') }}
                  />
                )}
              </Stack>

              {subtitleDetails.length || user.isOmnean ? (
                <Stack direction={{ xs: 'column', md: 'row' }} alignItems="center" gap={3}>
                  <Typography variant="body-medium" color="text.muted" textAlign={{ xs: 'center', md: 'left' }}>
                    {subtitleDetails.map((detail, index) => (
                      <Fragment key={index}>
                        {index > 0 && <> ∙ </>}
                        <span data-testid={detail.testId}>{detail.text}</span>
                      </Fragment>
                    ))}
                  </Typography>
                  <Stack direction="row" gap={1} my={-2}>
                    <CopyIdButton id={requestId} entityType="request" />
                    <CopyConfigButton config={request?.derivedFields.model} entityType="request" />
                  </Stack>
                </Stack>
              ) : null}
            </Stack>
          </>
        )}
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        gap={4}
        justifyContent="space-between"
        sx={{
          height: { md: '38px' }, // Title font-size * title line-height
          alignSelf: { md: 'flex-start' },
        }}
      >
        <InternalLinkIconButton href={generateRequestsListRoute()} sx={{ display: { md: 'none' }, padding: 0 }} size="small">
          <ArrowBack />
        </InternalLinkIconButton>
        {!isLoading && (
          <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} gap={{ xs: 1, md: 2 }}>
            {!isRequestCompleted && !isRequestRejected && !HIDE_REQUEST_ESTIMATED_SLA_DATE && (
              <EstimatedCompletionBanner requestId={requestId} />
            )}
            {operationalStatus && (
              <WithOverdueDays status={operationalStatus}>
                <OperationalStatusChip pillKey={operationalStatus.key} />
              </WithOverdueDays>
            )}
          </Stack>
        )}
        {isLoading && <Skeleton variant="rectangular" sx={{ borderRadius: '8px' }} width={240} height={36} />}
        <ManageMenu
          requestId={requestId}
          request={request}
          onDeleteRequest={onDeleteRequest}
          onRejectRequest={onRejectRequest}
          onUnrejectRequest={onUnrejectRequest}
          anchor={ManageMenuButton}
        />
      </Stack>
    </Stack>
  )
}
